import { useState } from "react";
import TypingEngine from "../components/TypingEngine";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DURATIONS = [30, 60, 120];

export default function TestPage() {
    const { user } = useAuth();
    const [lang, setLang] = useState("english");
    const [duration, setDuration] = useState(60);
    const [running, setRunning] = useState(false);
    const [text, setText] = useState("");
    const [result, setResult] = useState(null);
    const [key, setKey] = useState(0);

    const start = async () => {
        setResult(null);
        // Fetch a long text and repeat if needed
        const { data } = await api.get(`/test-text?language=${lang}`);
        // Repeat text a few times so the buffer is long enough for 120s tests
        const long = Array(6).fill(data.text).join(" ");
        setText(long);
        setRunning(true);
        setKey((k) => k + 1);
    };

    const handleComplete = async (r) => {
        setResult(r);
        setRunning(false);
        if (user) {
            try {
                await api.post("/results", {
                    language: lang,
                    mode: "test",
                    wpm: r.wpm,
                    accuracy: r.accuracy,
                    duration_seconds: r.durationSeconds,
                    correct_chars: r.correct,
                    incorrect_chars: r.incorrect,
                    total_chars: r.total,
                    text_snippet: text.slice(0, 80),
                });
            } catch (e) {
                // ignore
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
            <div>
                <p className="eyebrow">Timed Test</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-2">
                    Speed Test Arena
                </h1>
                <p className="text-[var(--lt-muted)] mt-3">
                    Pick a language, pick a duration, and go for your best.
                </p>
            </div>

            {!running && (
                <div className="glass rounded-2xl p-8 space-y-6" data-testid="test-setup-panel">
                    <div>
                        <p className="eyebrow mb-3">Language</p>
                        <div className="flex gap-3">
                            {["english", "nepali"].map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l)}
                                    className={`px-6 py-3 rounded-full border text-sm font-semibold transition-all ${
                                        lang === l
                                            ? "bg-[var(--lt-terracotta)] border-[var(--lt-terracotta)] text-white"
                                            : "border-[var(--lt-border)] text-[var(--lt-muted)]"
                                    }`}
                                    data-testid={`test-lang-${l}`}
                                >
                                    {l === "english" ? "English" : "नेपाली"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="eyebrow mb-3">Duration</p>
                        <div className="flex flex-wrap gap-3">
                            {DURATIONS.map((d) => (
                                <button
                                    key={d}
                                    onClick={() => setDuration(d)}
                                    className={`px-6 py-3 rounded-full border font-mono-stat font-semibold transition-all ${
                                        duration === d
                                            ? "bg-[var(--lt-terracotta)] border-[var(--lt-terracotta)] text-white"
                                            : "border-[var(--lt-border)] text-[var(--lt-muted)]"
                                    }`}
                                    data-testid={`test-duration-${d}s`}
                                >
                                    {d}s
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={start}
                        className="btn-terra"
                        data-testid="test-start-btn"
                    >
                        Start Test
                    </button>
                </div>
            )}

            {running && text && (
                <TypingEngine
                    key={key}
                    text={text}
                    language={lang}
                    mode="test"
                    durationSeconds={duration}
                    onComplete={handleComplete}
                />
            )}

            {result?.finalized && (
                <div
                    className="glass rounded-2xl p-8 border border-[var(--lt-terracotta)]"
                    data-testid="test-result-panel"
                >
                    <p className="eyebrow text-[var(--lt-terracotta)]">Test Complete</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-4">
                        <BigStat label="WPM" value={result.wpm.toFixed(1)} />
                        <BigStat label="Accuracy" value={`${result.accuracy.toFixed(1)}%`} />
                        <BigStat label="Duration" value={`${result.durationSeconds}s`} />
                        <BigStat label="Correct chars" value={result.correct} />
                    </div>
                    <button onClick={start} className="btn-terra mt-6" data-testid="test-retake-btn">
                        Try Again
                    </button>
                </div>
            )}
        </div>
    );
}

function BigStat({ label, value }) {
    return (
        <div>
            <div className="eyebrow">{label}</div>
            <div className="font-mono-stat text-4xl mt-2">{value}</div>
        </div>
    );
}
